import { dashboardService } from "../../../services/dashboard.service.js";
import { store } from "../../store.js";
import { setLoading } from "../../user/reducers/auth.reducer.js";
import {
  addToDashboard,
  setDashboard,
  setRecommendations,
  setRecommendationsError,
  setRecommendationsLoading,
  updateDashboard,
} from "../reducers/dashboard.reducer.js";

const dispatch = store.dispatch;

function getAccountId() {
  return store.getState().accounts?.selectedAccount?.accountId?._id;
}

function getUserId() {
  return store.getState().auth?.currentUser?._id;
}

function unwrapItem(res) {
  return res?.item || res?.data?.item || res?.dashboard || res?.data?.dashboard || res?.data || res;
}

export async function getDashboardById(dashboardId) {
  try {
    const res = await dashboardService.getById(dashboardId);
    const data = unwrapItem(res);
    return data?.dashboard || data;
  } catch (err) {
    console.log("Erro ao buscar dashboard:", err);
    throw err;
  }
}

export async function updateDashboardOrder(dashboardId, newOrderData) {
  try {
    dispatch(setLoading(true));
    const accountId = getAccountId();
    const res = await dashboardService.update(dashboardId, newOrderData, accountId);
    const data = unwrapItem(res);
    const dashboard = data?.dashboard || data;
    dispatch(setDashboard({ dashboard }));
    return data;
  } catch (err) {
    console.error("Erro ao atualizar ordem do dashboard:", err);
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function loadDashboard(dashboardId) {
  try {
    dispatch(setLoading(true));
    const res = await dashboardService.getById(dashboardId);
    const data = unwrapItem(res);
    const dashboard = data?.dashboard || data;
    dispatch(setDashboard({ dashboard }));
    return dashboard;
  } catch (err) {
    console.log("Had issues loading", err);
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function addChart(board, newChart) {
  try {
    dispatch(setLoading(true));
    const accountId = getAccountId();
    const userId = getUserId();

    if (!board?._id) throw new Error("Board não encontrada.");
    if (!userId) throw new Error("Usuário não autenticado.");

    const payload = {
      ...newChart,
      boardId: board._id,
      userId,
      type: "chart",
    };

    const res = await dashboardService.create(payload, accountId);
    const item = unwrapItem(res);
    dispatch(addToDashboard({ item }));

    return item;
  } catch (err) {
    console.error("Erro ao adicionar gráfico:", err);
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function saveAccumulator(board, newAccumulator) {
  try {
    dispatch(setLoading(true));
    const accountId = getAccountId();
    const userId = getUserId();

    if (!board?._id) throw new Error("Board não encontrada.");
    if (!userId) throw new Error("Usuário não autenticado.");

    const payload = {
      ...newAccumulator,
      boardId: board._id,
      userId,
      type: "accumulator",
    };

    const res = await dashboardService.create(payload, accountId);
    const item = unwrapItem(res);
    dispatch(addToDashboard({ item }));

    return item;
  } catch (err) {
    console.error("Erro ao adicionar acumulador:", err);
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function removeItemDashboard(dashboardId, itemId) {
  try {
    const accountId = getAccountId();
    const res = await dashboardService.removeItem(dashboardId, itemId, accountId);
    const updated = unwrapItem(res);
    const dashboard = updated?.dashboard || updated;
    dispatch(updateDashboard({ dashboard }));
    return dashboard;
  } catch (err) {
    console.error("Erro ao remover item do dashboard:", err);
    throw err;
  }
}

export async function chartUpdate(dashboard, chartId, chartData) {
  try {
    const accountId = getAccountId();
    if (!dashboard?._id) throw new Error("Dashboard não carregado.");

    const res = await dashboardService.updateItem(dashboard._id, chartId, chartData, accountId);
    const payload = unwrapItem(res);

    const returnedDashboard = payload?.dashboard || payload?.data?.dashboard;
    if (returnedDashboard?._id) {
      dispatch(setDashboard({ dashboard: returnedDashboard }));
      return returnedDashboard;
    }

    const returnedItem =
      payload?.item ||
      payload?.data?.item ||
      payload?.updatedItem ||
      payload?.chart ||
      payload?.data?.chart;

    const currentDashboard = store.getState().dashboard?.dashboard || dashboard;

    const updatedDynamicData = (currentDashboard?.dynamicData || []).map((item) =>
      String(item?._id) === String(chartId)
        ? { ...item, ...(returnedItem || {}), ...chartData }
        : item
    );

    const nextDashboard = {
      ...currentDashboard,
      dynamicData: updatedDynamicData,
    };

    dispatch(setDashboard({ dashboard: nextDashboard }));

    return payload;
  } catch (err) {
    console.error("Erro ao atualizar gráfico:", err);
    throw err;
  }
}

export async function accumulatorUpdate(dashboard, id, acumuladorData) {
  try {
    const accountId = getAccountId();
    if (!dashboard?._id) throw new Error("Dashboard não carregado.");

    const res = await dashboardService.updateItem(dashboard._id, id, acumuladorData, accountId);
    const payload = unwrapItem(res);

    const returnedDashboard = payload?.dashboard || payload?.data?.dashboard;
    if (returnedDashboard?._id) {
      dispatch(setDashboard({ dashboard: returnedDashboard }));
      return returnedDashboard;
    }

    const returnedItem =
      payload?.item ||
      payload?.data?.item ||
      payload?.updatedItem ||
      payload?.accumulator ||
      payload?.data?.accumulator;

    const currentDashboard = store.getState().dashboard?.dashboard || dashboard;

    const updatedDynamicData = (currentDashboard?.dynamicData || []).map((item) =>
      String(item?._id) === String(id)
        ? { ...item, ...(returnedItem || {}), ...acumuladorData }
        : item
    );

    const nextDashboard = {
      ...currentDashboard,
      dynamicData: updatedDynamicData,
    };

    dispatch(setDashboard({ dashboard: nextDashboard }));

    return payload;
  } catch (err) {
    console.error("Erro ao atualizar acumulador:", err);
    throw err;
  }
}

export async function chartFormat(chartId) {
  try {
    dispatch(setLoading(true));
    return await dashboardService.getChartData(chartId);
  } catch (err) {
    console.log("Had issues loading", err);
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function accumulatorFormat(id) {
  try {
    dispatch(setLoading(true));
    return await dashboardService.getAccumulatorData(id);
  } catch (err) {
    console.log("Had issues loading", err);
    throw err;
  } finally {
    dispatch(setLoading(false));
  }
}

export async function getChartRecommendations(boardId, { includePreview = true, limit = 10 } = {}) {
  try {
    dispatch(setRecommendationsLoading(true));
    dispatch(setRecommendationsError(null));

    const res = await dashboardService.getChartRecommendations(boardId, { includePreview, limit });
    const data = unwrapItem(res);

    dispatch(setRecommendations(data));
    return data;
  } catch (err) {
    const msg = err?.response?.data?.error || err?.message || "Erro ao buscar recomendações";
    dispatch(setRecommendationsError(msg));
    throw err;
  } finally {
    dispatch(setRecommendationsLoading(false));
  }
}